import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { FadeUp, MaskedLines } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const budgets = ["Under €10k", "€10k to €25k", "€25k to €75k", "€75k+", "To be scoped"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: "corporate", budget: budgets[4], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "Contact · Haus Ov Harii";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry received. Expect a reply within 48 hours.");
      setForm({ name: "", email: "", inquiry_type: form.inquiry_type, budget: budgets[4], message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white placeholder:text-[#616775] transition-colors focus:border-umber focus:outline-none";

  return (
    <div className="grid gap-16 px-6 pb-10 pt-32 lg:grid-cols-12 lg:px-12" data-testid="contact-page">
      <div className="lg:col-span-5">
        <p className="eyebrow mb-6">Contact</p>
        <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
          <MaskedLines lines={["Start a", "conversation."]} delay={0.1} />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-[#9EA3B0]">
            Corporate engagements and creative commissions both begin the same way: a short,
            honest note about what you are trying to change.
          </p>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-12 space-y-8">
          <div>
            <p className="eyebrow mb-2">Direct</p>
            <p className="text-sm leading-relaxed text-[#9EA3B0]" data-testid="contact-direct-note">
              All enquiries arrive through the form. It lands straight in my inbox and I reply personally.
            </p>
          </div>
          <div>
            <p className="eyebrow mb-2">Studio</p>
            <p className="text-sm text-[#9EA3B0]">Remote, working worldwide</p>
          </div>
          <div>
            <p className="eyebrow mb-2">Availability</p>
            <p className="flex items-center gap-3 text-sm text-[#9EA3B0]" data-testid="availability-indicator">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              Currently booking new commissions
            </p>
          </div>
          <div>
            <p className="eyebrow mb-2">Elsewhere</p>
            <div className="flex gap-6 text-sm">
              <a data-testid="social-linkedin-link" href="https://www.linkedin.com/in/harriet-griffiths-jones-51a155126/" target="_blank" rel="noopener noreferrer" className="text-[#9EA3B0] transition-colors hover:text-white">LinkedIn</a>
            </div>
          </div>
        </FadeUp>
      </div>

      <FadeUp delay={0.2} className="lg:col-span-7">
        <form onSubmit={submit} className="border border-white/10 p-6 sm:p-10" data-testid="contact-form">
          <p className="eyebrow mb-4">I am enquiring about</p>
          <div className="grid grid-cols-2 gap-px border border-white/15 bg-white/15" role="group" aria-label="Inquiry type">
            {[
              ["corporate", "A Corporate engagement"],
              ["creative", "A Creative commission"],
            ].map(([val, label]) => (
              <button
                key={val}
                type="button"
                data-testid={`contact-type-${val}`}
                aria-pressed={form.inquiry_type === val}
                onClick={() => setForm((f) => ({ ...f, inquiry_type: val }))}
                className={`px-4 py-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  form.inquiry_type === val ? "bg-umber text-[#0B0C0E]" : "bg-[#0B0C0E] text-[#9EA3B0] hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="eyebrow mb-2 block">Name</label>
              <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
            </div>
            <div>
              <label htmlFor="contact-email" className="eyebrow mb-2 block">Email</label>
              <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="contact-budget" className="eyebrow mb-2 block">Indicative budget</label>
            <select id="contact-budget" data-testid="contact-budget-select" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none`}>
              {budgets.map((b) => (
                <option key={b} value={b} className="bg-[#121418]">{b}</option>
              ))}
            </select>
          </div>

          <div className="mt-6">
            <label htmlFor="contact-message" className="eyebrow mb-2 block">The situation</label>
            <textarea id="contact-message" data-testid="contact-message-input" required rows={6} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="What are you trying to change, and by when?" />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={sending}
            className="mt-8 inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#0B0C0E] transition-all duration-300 hover:bg-umber disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send enquiry"} <Send size={14} />
          </button>
        </form>
      </FadeUp>
    </div>
  );
}
